import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useTeam } from '@/hooks/useTeam';
import { usePipelines } from '@/hooks/usePipelines';
import { useLossReasons } from '@/hooks/useLossReasons';
import { Loader as Loader2 } from 'lucide-react';
import type { ContactOpportunity } from '@/hooks/useContactDetails';
import {
  dateInputToIso,
  isFutureDateInputValue,
  isoToDateInputValue,
  todayDateInputValue,
} from '@/lib/dateInput';

export interface OpportunityEditData {
  title: string;
  pipeline_id: string;
  stage_id: string;
  product?: string;
  price?: number;
  status: 'open' | 'won' | 'lost';
  closed_at?: string;
  sale_date?: string;
  won_by_user_id?: string;
  loss_reason_id?: string;
  notes?: string;
}

interface EditOpportunityFullDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: OpportunityEditData) => Promise<void>;
  opportunity?: ContactOpportunity | null;
}

export function EditOpportunityFullDialog({
  open,
  onClose,
  onSave,
  opportunity,
}: EditOpportunityFullDialogProps) {
  const { members, loading: loadingMembers } = useTeam();
  const { pipelines, pipelineStages } = usePipelines();
  const { lossReasons, loading: loadingReasons } = useLossReasons();

  const [title, setTitle] = useState('');
  const [pipelineId, setPipelineId] = useState('');
  const [stageId, setStageId] = useState('');
  const [product, setProduct] = useState('');
  const [price, setPrice] = useState('');
  const [closedAt, setClosedAt] = useState('');
  const [saleDate, setSaleDate] = useState('');
  const [wonByUserId, setWonByUserId] = useState('');
  const [lossReasonId, setLossReasonId] = useState('');
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const derivedStatus: 'open' | 'won' | 'lost' = (() => {
    if (!stageId) return 'open';
    const stage = pipelineStages.find(s => s.id === stageId);
    if (!stage) return 'open';
    if (stage.stage_type === 'won') return 'won';
    if (stage.stage_type === 'lost') return 'lost';
    return 'open';
  })();

  useEffect(() => {
    if (open && opportunity) {
      setTitle(opportunity.title || '');
      setPipelineId(opportunity.pipeline_id || '');
      setStageId(opportunity.pipeline_stage_id || '');
      setProduct(opportunity.product || '');
      setPrice(opportunity.price ? String(opportunity.price) : '');
      setClosedAt(isoToDateInputValue(opportunity.closed_at));
      setSaleDate(isoToDateInputValue(opportunity.sale_date));
      setWonByUserId(opportunity.won_by_user_id || '');
      setLossReasonId(opportunity.loss_reason_id || '');
      setNotes(opportunity.notes || '');
    }
  }, [open, opportunity]);

  const filteredStages = pipelineId
    ? pipelineStages.filter((s) => s.pipeline_id === pipelineId)
    : [];

  const activeMembers = members.filter((m) => m.is_active);

  const today = todayDateInputValue();
  const closedAtIsFuture =
    derivedStatus !== 'open' && isFutureDateInputValue(closedAt);
  const saleDateIsFuture =
    derivedStatus === 'won' && isFutureDateInputValue(saleDate);
  const hasFutureDate = closedAtIsFuture || saleDateIsFuture;

  const handlePipelineChange = (newPipelineId: string) => {
    setPipelineId(newPipelineId);
    const stages = pipelineStages.filter((s) => s.pipeline_id === newPipelineId);
    if (stages.length > 0) {
      setStageId(stages[0].id);
    } else {
      setStageId('');
    }
  };

  const handleStageChange = (newStageId: string) => {
    setStageId(newStageId);
    const stage = pipelineStages.find(s => s.id === newStageId);
    if (!stage || stage.stage_type === 'normal') {
      setClosedAt('');
      setSaleDate('');
      setWonByUserId('');
      setLossReasonId('');
    } else if (stage.stage_type === 'won') {
      setLossReasonId('');
      if (!closedAt) {
        setClosedAt(todayDateInputValue());
      }
    } else if (stage.stage_type === 'lost') {
      setSaleDate('');
      setWonByUserId('');
      if (!closedAt) {
        setClosedAt(todayDateInputValue());
      }
    }
  };

  const handleSave = async () => {
    if (!title.trim() || !pipelineId || !stageId || hasFutureDate) return;

    setIsSubmitting(true);

    const data: OpportunityEditData = {
      title: title.trim(),
      pipeline_id: pipelineId,
      stage_id: stageId,
      product: product.trim() || undefined,
      price: price ? parseFloat(price) : undefined,
      status: derivedStatus,
      closed_at: dateInputToIso(closedAt),
      sale_date: derivedStatus === 'won' ? dateInputToIso(saleDate) : undefined,
      won_by_user_id: derivedStatus === 'won' && wonByUserId ? wonByUserId : undefined,
      loss_reason_id: derivedStatus === 'lost' && lossReasonId ? lossReasonId : undefined,
      notes: notes.trim() || undefined,
    };

    try {
      await onSave(data);
      handleClose();
    } catch (error) {
      console.error('Error saving opportunity:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setTitle('');
    setPipelineId('');
    setStageId('');
    setProduct('');
    setPrice('');
    setClosedAt('');
    setSaleDate('');
    setWonByUserId('');
    setLossReasonId('');
    setNotes('');
    onClose();
  };

  const formatCurrency = (value: string) => {
    const numericValue = value.replace(/\D/g, '');
    if (!numericValue) return '';
    const number = parseInt(numericValue, 10) / 100;
    return number.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value.replace(/\D/g, '');
    if (!rawValue) {
      setPrice('');
      return;
    }
    const numericValue = parseInt(rawValue, 10) / 100;
    setPrice(String(numericValue));
  };

  const dialogShell =
    "rounded-2xl border border-[#E5E7EB] bg-white text-[#0F172A] max-w-lg max-h-[90vh] overflow-y-auto shadow-sm dark:border-[#3F3F46] dark:bg-[#27272A] dark:text-[#FAFAFA] dark:shadow-none";
  const labelClass = "text-[#0F172A] dark:text-[#FAFAFA]";
  const inputClass =
    "h-11 rounded-xl border border-[#E5E7EB] bg-white text-[#0F172A] placeholder:text-[#94A3B8] focus-visible:border-[#2563EB] transition-colors duration-200 dark:border-[#3F3F46] dark:bg-[#18181B] dark:text-[#FAFAFA] dark:placeholder:text-[#71717A] dark:focus-visible:border-[#60A5FA]";
  const selectClass =
    "w-full rounded-xl border border-[#E5E7EB] bg-white text-[#0F172A] transition-colors duration-200 focus:border-[#2563EB] dark:border-[#3F3F46] dark:bg-[#18181B] dark:text-[#FAFAFA] dark:focus:border-[#60A5FA]";
  const outlineBtn =
    "rounded-xl border border-[#E5E7EB] bg-white text-[#334155] hover:bg-[#F1F5F9] transition-colors duration-200 dark:border-[#3F3F46] dark:bg-transparent dark:text-[#D4D4D8] dark:hover:bg-[#3F3F46]";

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className={dialogShell}>
        <DialogHeader>
          <DialogTitle className="text-[#0F172A] dark:text-[#FAFAFA]">Editar Oportunidade</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label className={labelClass}>Titulo *</Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Titulo da oportunidade"
              className={inputClass}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className={labelClass}>Pipeline *</Label>
              <Select value={pipelineId} onValueChange={handlePipelineChange}>
                <SelectTrigger className={selectClass}>
                  <SelectValue placeholder="Selecione" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    {pipelines.map((pipeline) => (
                      <SelectItem key={pipeline.id} value={pipeline.id}>
                        {pipeline.name}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className={labelClass}>Etapa *</Label>
              <Select value={stageId} onValueChange={handleStageChange} disabled={!pipelineId}>
                <SelectTrigger className={selectClass}>
                  <SelectValue placeholder="Selecione" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    {filteredStages.map((stage) => (
                      <SelectItem key={stage.id} value={stage.id}>
                        {stage.name}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label className={labelClass}>Produto/Servico</Label>
            <Input
              value={product}
              onChange={(e) => setProduct(e.target.value)}
              placeholder="Nome do produto ou servico"
              className={inputClass}
            />
          </div>

          <div className="space-y-2">
            <Label className={labelClass}>Valor (R$)</Label>
            <Input
              value={price ? formatCurrency(String(Math.round(parseFloat(price) * 100))) : ''}
              onChange={handlePriceChange}
              placeholder="0,00"
              className={inputClass}
            />
          </div>

          {(derivedStatus === 'won' || derivedStatus === 'lost') && (
            <div className="space-y-2">
              <Label className={labelClass}>Data de Fechamento</Label>
              <Input
                type="date"
                value={closedAt}
                max={today}
                onChange={(e) => setClosedAt(e.target.value)}
                aria-invalid={closedAtIsFuture}
                className={`${inputClass} [color-scheme:light] dark:[color-scheme:dark]`}
              />
              {closedAtIsFuture && (
                <p className="text-sm text-red-500 dark:text-red-400">
                  A data de fechamento nao pode ser futura.
                </p>
              )}
            </div>
          )}

          {derivedStatus === 'won' && (
            <>
              <div className="space-y-2">
                <Label className={labelClass}>Data da Venda</Label>
                <Input
                  type="date"
                  value={saleDate}
                  max={today}
                  onChange={(e) => setSaleDate(e.target.value)}
                  aria-invalid={saleDateIsFuture}
                  className={`${inputClass} [color-scheme:light] dark:[color-scheme:dark]`}
                />
                {saleDateIsFuture && (
                  <p className="text-sm text-red-500 dark:text-red-400">
                    A data da venda nao pode ser futura.
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label className={labelClass}>Responsavel pela Venda</Label>
                <Select value={wonByUserId || 'none'} onValueChange={(v) => setWonByUserId(v === 'none' ? '' : v)} disabled={loadingMembers}>
                  <SelectTrigger className={selectClass}>
                    <SelectValue placeholder={loadingMembers ? 'Carregando...' : 'Selecione'} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectItem value="none">Nenhum</SelectItem>
                      {activeMembers.map((member) => (
                        <SelectItem key={member.id} value={member.id}>
                          {member.name || member.email}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>
            </>
          )}

          {derivedStatus === 'lost' && (
            <div className="space-y-2">
              <Label className={labelClass}>Motivo da Perda</Label>
              <Select value={lossReasonId || 'none'} onValueChange={(v) => setLossReasonId(v === 'none' ? '' : v)} disabled={loadingReasons}>
                <SelectTrigger className={selectClass}>
                  <SelectValue placeholder={loadingReasons ? 'Carregando...' : 'Selecione o motivo'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem value="none">Nenhum</SelectItem>
                    {lossReasons.map((reason) => (
                      <SelectItem key={reason.id} value={reason.id}>
                        {reason.name}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="space-y-2">
            <Label className={labelClass}>Anotacoes</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Observacoes sobre a oportunidade..."
              className="min-h-[80px] resize-none rounded-xl border border-[#E5E7EB] bg-white text-[#0F172A] placeholder:text-[#94A3B8] focus-visible:border-[#2563EB] transition-colors duration-200 dark:border-[#3F3F46] dark:bg-[#18181B] dark:text-[#FAFAFA] dark:placeholder:text-[#71717A] dark:focus-visible:border-[#60A5FA]"
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleClose}
            className={outlineBtn}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSave}
            disabled={isSubmitting || !title.trim() || !pipelineId || !stageId || hasFutureDate}
            className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white transition-colors duration-200"
          >
            {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Salvar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
